// The events of the SSE stream as typed values, parsed with the schemas `just gen` writes from
// the backend's frame model (spec ST1). A frame that is not JSON or fails its schema parses to
// undefined; it never throws (audit frontend A.3: an unguarded JSON.parse).
import { z } from "zod";

import {
  AchievementUnlockedFrame,
  EditProjectSavedFrame,
  JobEventFrame,
  StreamEventName,
} from "./generated/zod";

// A status the API adds after this build still refetches the job: the frame keeps any status
// string, and event-handlers.ts patches the cache only with a status this build knows, the way
// ApiError keeps a code this build does not know (F3 decision 18).
const JobFrame = JobEventFrame.extend({ status: z.string().min(1) });

/** The `data` schema of each event name in the contract. A name the server adds fails tsc here
 * until it has a schema, then in event-handlers.ts until it has a case. */
const streamEventSchemas = {
  "job.transitioned": JobFrame,
  // Refetch the team's jobs; the data is `{}`.
  resync: z.object({}),
  // A project of the team was saved at `version` (spec editor/live-sync-stream.md).
  "edit_project.saved": EditProjectSavedFrame,
  // One of the signed-in user's achievements unlocked in the team: the achievement and the award,
  // on this user's streams alone (spec journey/achievements.md, 4).
  achievement_unlocked: AchievementUnlockedFrame,
} satisfies Record<StreamEventName, z.ZodType>;

export type StreamEvent = {
  [N in StreamEventName]: { name: N; id: string; data: z.output<(typeof streamEventSchemas)[N]> };
}[StreamEventName];

export const STREAM_EVENT_NAMES = StreamEventName.options;

export function parseStreamEvent(
  name: StreamEventName,
  data: string,
  id: string,
): StreamEvent | undefined {
  let json: unknown;
  try {
    json = data === "" ? {} : JSON.parse(data);
  } catch {
    return undefined;
  }
  const result = streamEventSchemas[name].safeParse(json);
  // The cast pairs `name` with its own schema's output, which TypeScript cannot follow.
  return result.success ? ({ name, id, data: result.data } as StreamEvent) : undefined;
}
